import './SeePitchesBy.scss';

import { Checkbox, Divider, Radio, RadioGroup, Typography } from '@trackman/web-shared-components';
import { PitchType, PitchTypeGroup, PitchGrouping } from 'types/CodegenReportTypes';

import { useTranslation } from 'react-i18next';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import Dot from 'components/Dot/Dot';
import { pitchTypeGroupToColor, pitchTypeToColor } from 'utilities/colorHelpers';
import { configurablePitchTypeGroups, configurablePitchTypes } from 'utilities/constants';

type Props = {
  seePitchesBy: PitchGrouping;
  setSeePitchesBy: (seePitchesBy: PitchGrouping) => void;

  pitchTypes: PitchType[];
  availablePitchTypes?: PitchType[];
  setPitchTypes: (pitchTypes: PitchType[]) => void;

  pitchTypeGroups: PitchTypeGroup[];
  availablePitchTypeGroups?: PitchTypeGroup[];
  setPitchTypeGroups: (pitchTypeGroups: PitchTypeGroup[]) => void;

  disabled: boolean;
};

const SeePitchesBy = ({
  seePitchesBy,
  setSeePitchesBy,
  pitchTypes,
  availablePitchTypes = configurablePitchTypes,
  setPitchTypes,
  pitchTypeGroups,
  availablePitchTypeGroups = configurablePitchTypeGroups,
  setPitchTypeGroups,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const [parent] = useAutoAnimate();

  return (
    <>
      <Typography variant='label bold' component='div' color='#A0A0A0'>
        {t('See pitches by:')}
      </Typography>
      <RadioGroup
        style={{ gap: '16px' }}
        value={seePitchesBy}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setSeePitchesBy((event.target as HTMLInputElement).value as PitchGrouping)
        }
      >
        <div className='SeePitchesBy-container' ref={parent}>
          <Radio value={'Types' satisfies PitchGrouping} label={t('Types')} disabled={disabled} />
          {seePitchesBy === 'Types' &&
            configurablePitchTypes.sort().map((pitchType) => (
              <div className='SeePitchesBy-checkbox' key={pitchType}>
                <Checkbox
                  label={t(pitchType)}
                  checked={pitchType === 'All' ? configurablePitchTypes.length === pitchTypes.length : pitchTypes.includes(pitchType)}
                  onChange={() => {
                    if (pitchType === 'All') {
                      if (configurablePitchTypes.length === pitchTypes.length) {
                        setPitchTypes([]);
                      } else {
                        setPitchTypes(configurablePitchTypes);
                      }
                    } else if (pitchTypes.includes(pitchType)) {
                      setPitchTypes(pitchTypes.filter((item) => item !== pitchType && item !== 'All'));
                    } else {
                      const selectedPitchTypes = [...pitchTypes, pitchType];
                      if (configurablePitchTypes.length - 1 === selectedPitchTypes.length) {
                        selectedPitchTypes.push('All');
                      }
                      setPitchTypes(selectedPitchTypes);
                    }
                  }}
                  disabled={disabled || (!availablePitchTypes.includes(pitchType) && pitchType !== 'All')}
                />
                <Dot color={pitchTypeToColor[pitchType]} />
              </div>
            ))}
          <Radio value={'Groups' satisfies PitchGrouping} label={t('Groups')} disabled={disabled} />
          {seePitchesBy === 'Groups' &&
            configurablePitchTypeGroups.sort().map((pitchTypeGroup) => (
              <div className='SeePitchesBy-checkbox' key={pitchTypeGroup}>
                <Checkbox
                  label={t(pitchTypeGroup)}
                  checked={
                    pitchTypeGroup === 'All'
                      ? configurablePitchTypeGroups.length === pitchTypeGroups.length
                      : pitchTypeGroups.includes(pitchTypeGroup)
                  }
                  onChange={() => {
                    if (pitchTypeGroup === 'All') {
                      if (configurablePitchTypeGroups.length === pitchTypeGroups.length) {
                        setPitchTypeGroups([]);
                      } else {
                        setPitchTypeGroups(configurablePitchTypeGroups);
                      }
                    } else if (pitchTypeGroups.includes(pitchTypeGroup)) {
                      setPitchTypeGroups(pitchTypeGroups.filter((item) => item !== pitchTypeGroup && item !== 'All'));
                    } else {
                      const selectedPitchTypeGroups = [...pitchTypeGroups, pitchTypeGroup];
                      if (configurablePitchTypeGroups.length - 1 === selectedPitchTypeGroups.length) {
                        selectedPitchTypeGroups.push('All');
                      }
                      setPitchTypeGroups(selectedPitchTypeGroups);
                    }
                  }}
                  disabled={disabled || (!availablePitchTypeGroups.includes(pitchTypeGroup) && pitchTypeGroup !== 'All')}
                />
                <Dot color={pitchTypeGroupToColor[pitchTypeGroup]} />
              </div>
            ))}
        </div>
      </RadioGroup>
      <Divider />
    </>
  );
};

export default SeePitchesBy;
